/* eslint class-methods-use-this: 0 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { Glyphicon, Modal } from 'react-bootstrap';

import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import { withTranslation, Trans, useTranslation } from 'react-i18next';
import reasonCodes from 'config/reason-codes';
import AppointmentPicker from 'components/ui/tailwind/AppointmentPicker';
import SessionStore from 'stores/SessionStore';
import { isDrayageShipment } from 'helpers/shipment-types';
import SelectReasonCodes from 'components/modules/marketplace-config/SelectReasonCodes';
import CargomaticModal from 'components/v2/modal';
import { titleize } from 'underscore.string';
import SoftAlertHoldNotification from 'components/modules/shipments/stops/SoftAlertHoldNotification';
import { getLocationTimezone } from '../../../../helpers/locations';
import CargomaticSelect from '../../../v2/select';

const ERRORS = {
  END_BEFORE_START: 'START_DATE_BEFORE_END_DATE',
};

const DRAYAGE_DEFAULT_WINDOW_HOURS = 1;
const SHORTHAUL_DEFAULT_WINDOW_HOURS = 4;

const getInitialState = (props) => {
  const { stop, shipment } = props;

  const { shipment_type, isStaging } = shipment;

  const drayageShipment = isDrayageShipment(shipment);

  const { empty, window_start, window_end } = stop;

  let timezone;
  if (isStaging) {
    timezone = props.selected_timezone;
  } else {
    timezone = getLocationTimezone(stop.location);
  }
  const today = moment.tz(moment(), timezone);
  const date = today.toDate();
  const startHour = date.getHours() + Math.round(date.getMinutes() / 60) + 1;

  date.setHours(startHour, 0, 0);

  const defaultEnd = today.clone();
  const endDate = defaultEnd.toDate();
  const endHour = startHour + 1;
  endDate.setHours(endHour, 0, 0);

  const momentStartDate = window_start
    ? moment.tz(window_start, timezone)
    : moment.tz(date, timezone);
  const momentEndDate = window_end
    ? moment.tz(window_end, timezone)
    : moment.tz(endDate, timezone);

  let start;
  let end;

  if (momentStartDate && momentStartDate.isValid()) {
    start = momentStartDate;
  } else {
    start = null;
  }

  if (momentEndDate && momentEndDate.isValid()) {
    end = momentEndDate;
  } else {
    end = null;
  }

  let appointment_unconfirmed = stop.appointment_unconfirmed;
  if (!start && !end) {
    appointment_unconfirmed = true;
  }

  if (empty || !drayageShipment) {
    appointment_unconfirmed = false;
  }

  return {
    drayageShipment,
    timezone,
    start,
    end,
    appointment_unconfirmed,
    hideConfirm: stop.empty || shipment_type !== 'drayage',
    errors: [],
    isValid: true,
    name: '',
    value: '',
    reason: null,
    comment: '',
    commentRequired: false,
    requireCodes: false,
  };
};

const ErrorList = (props) => {
  const { t } = useTranslation();
  return (
    (<div
      data-testid="div-8b1fcfd5"
      className="relative px-4 py-3 text-red-700 border border-red-400 rounded bg-red-50"
      role="alert">
      <h5 data-testid="h5-b0d664cc">{t('FIX_BELOW_ERRORS')}</h5>
      <div data-testid="div-092338e5">
        <ul data-testid="ul-97602bf9" className="ml-4">
          {props.errors.map((error, i) => {
            return <li data-testid={`li-a6414d92-${i}`} key={i}>{`* ${t(error)}`}</li>;
          })}
        </ul>
      </div>
    </div>)
  );
};

ErrorList.propTypes = {
  errors: PropTypes.array,
};

ErrorList.defaultProps = {
  errors: [],
};

class AddAppointmentModal extends Component {
  static propTypes = {
    show: PropTypes.bool.isRequired,
    onSave: PropTypes.func.isRequired,
    onHide: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = getInitialState(props);
  }

  setCodesRequired = (requireCodes) => {
    this.setState({ requireCodes });
  };

  handleReasonSelect = (reason) => {
    const { code, description, reasonType } = reason;

    this.setState(
      {
        name: description,
        value: code,
        reason_type: reasonType,
      },
      this.validate,
    );
  };

  handleCommentInput = (comment) => {
    this.setState({ comment }, this.validate);
  };

  handleCommentRequired = (value) => {
    this.setState({ commentRequired: value }, this.validate);
  };

  handleSave = () => {
    const {
      start,
      end,
      appointment_unconfirmed,
      timezone,
      name,
      value,
      reason_type,
      comment,
    } = this.state;
    let window_start;
    let window_end;
    if (timezone) {
      window_start = start
        ? moment.utc(moment(start.tz(timezone, true)).format())
        : null;

      window_end = end
        ? moment.utc(moment(end).tz(timezone, true).format())
        : null;
    } else {
      window_start = start ? moment.utc(moment(start).format()) : null;
      window_end = end ? moment.utc(moment(end).format()) : null;
    }

    const updates = {
      window_start,
      window_end,
      appointment: true,
      appointment_unconfirmed,
      name,
      value,
      reason_type,
    };

    if (comment && comment.length) {
      updates.comment = comment;
    }

    if (!appointment_unconfirmed) {
      updates.appointment_unconfirmed = false;
    }

    if (!window_start && !window_end) {
      updates.appointment = false;
      updates.appointment_unconfirmed = true;
    }

    this.props.onSave(updates);
  };

  handleToggleConfirmed = (confirmed) =>
    this.setState({ appointment_unconfirmed: !confirmed }, () =>
      this.validate(),
    );

  handleStartDateChange = (start) => {
    this.setState(
      (prevState) => {
        const { end: currentEnd } = prevState;

        let end = currentEnd;

        const momentStart = moment(start);
        if (start) {
          const hours = prevState.drayageShipment
            ? DRAYAGE_DEFAULT_WINDOW_HOURS
            : SHORTHAUL_DEFAULT_WINDOW_HOURS;
          if (!end) {
            end = momentStart.add(hours, 'hours');
          }
        }

        let { appointment_unconfirmed } = prevState;
        if (!prevState.hideConfirm && (!end || !start)) {
          appointment_unconfirmed = true;
        }

        return {
          start,
          end,
          appointment_unconfirmed,
        };
      },
      () => this.validate(),
    );
  };

  handleEndDateChange = (end) => {
    this.setState(
      (prevState) => {
        const { start: currentStart } = prevState;

        let start = currentStart;

        const momentEnd = moment(end);

        if (end) {
          const hours = prevState.drayageShipment
            ? DRAYAGE_DEFAULT_WINDOW_HOURS
            : SHORTHAUL_DEFAULT_WINDOW_HOURS;
          if (!start) {
            start = momentEnd.subtract(hours, 'hours');
          }
        }

        let { appointment_unconfirmed } = prevState;
        if (!prevState.hideConfirm && (!end || !start)) {
          appointment_unconfirmed = true;
        }

        return {
          appointment_unconfirmed,
          start,
          end,
        };
      },
      () => this.validate(),
    );
  };

  validate = () => {
    const { requireCodes, name, comment, commentRequired } = this.state;

    const errors = [];
    let isValid = true;

    if (
      this.state.start &&
      this.state.end &&
      moment(this.state.end).isBefore(this.state.start)
    ) {
      errors.push(ERRORS.END_BEFORE_START);
      isValid = false;
    }

    if (requireCodes && !name) {
      errors.push('REASON_CODE_REQUIRED_TO_UPDATE_APPOINMENT');

      if (commentRequired && (!comment || !comment.length)) {
        errors.push('REASON_CODE_COMMENT_REQUIRED');
      }
    }

    this.setState({ errors, isValid });
  };

  renderSoftAlertForHolds() {
    const { shipment } = this.props;
    if (!shipment || !shipment.holds) return null;
    return <SoftAlertHoldNotification shipment={shipment} />;
  }

  renderFooter() {
    const { drayageShipment } = this.state;
    const { t } = this.props;

    const hours = drayageShipment
      ? DRAYAGE_DEFAULT_WINDOW_HOURS
      : SHORTHAUL_DEFAULT_WINDOW_HOURS;
    const unit = t('HOUR', { count: hours });
    const hoursText = `${hours} ${unit}`;

    return (
      (<div data-testid="div-47ae53b5" className="flex justify-end">
        <div data-testid="div-20a4b291" className="flex button-group">
          <button
            data-testid="button-1e1d505e"
            className="block button secondary-action"
            onClick={this.props.onHide}>
            {t('CANCEL')}
          </button>
          <button
            data-testid="button-964027fe"
            className={`block mr-2 button button-green carrier-shipmentDetailDateItem-editAppointment heap ${
              this.props.stop.heapClassName || ''
            }`}
            disabled={!this.state.isValid}
            onClick={this.handleSave}>
            {titleize(t('SAVE'))}
          </button>
        </div>
      </div>)
    );
  }
  render() {
    const { drayageShipment } = this.state;
    const { t, dataTestId } = this.props;

    const hours = drayageShipment
      ? DRAYAGE_DEFAULT_WINDOW_HOURS
      : SHORTHAUL_DEFAULT_WINDOW_HOURS;
    const unit = t('HOUR', { count: hours });
    const hoursText = `${hours} ${unit}`;

    return (
      (<CargomaticModal
        dataTestId={dataTestId}
        show={this.props.show}
        onHide={this.props.onHide}
        style={{ top: '25%' }}
        title={t('ADD_EDIT_APPOINTMENT')}
        footer={this.renderFooter()}>
        {this.renderSoftAlertForHolds()}
        <div
          data-testid="div-49343493"
          className="py-4 text-center bg-grey-darkest lg:px-4">
          <div
            data-testid="div-52793270"
            className="flex items-center p-2 leading-none text-white bg-green lg:rounded-full lg:inline-flex"
            role="alert">
            <span
              data-testid="span-1cc29e96"
              className="flex px-2 py-1 pr-1 text-sm font-bold uppercase rounded-full bg-green">
              {t('PRO_TIP')}
            </span>
            <Glyphicon className="pr-2 text-xs" glyph="pencil" />
            <div data-testid="div-c0deccc0" className="flex">
              <span data-testid="span-9433fabe" className="text-xsm">
                <Trans
                  i18nKey="APPOINTMENT_WINDOW_MESSAGE"
                  values={{
                    hoursText,
                  }}
                  components={[<strong data-testid="strong-eeab8f70" />]}
                />
              </span>
            </div>
          </div>
        </div>
        {this.state.errors.length > 0 && (
          <ErrorList errors={this.state.errors} />
        )}
        <div data-testid="div-109d366c" className="p-4">
          <AppointmentPicker
            dataTestId="AppointmentPicker-9e447079"
            start={this.state.start}
            end={this.state.end}
            confirmed={!this.state.appointment_unconfirmed}
            selected_timezone={this.state.timezone}
            storeTimeZoneChange={this.storeTimeZoneChange}
            onStartChange={this.handleStartDateChange}
            onEndChange={this.handleEndDateChange}
            onToggleConfirmed={this.handleToggleConfirmed}
            hideConfirm={this.state.hideConfirm} />
        </div>
        {SessionStore.isAdmin() && (
          <SelectReasonCodes
            shipment={this.props.shipment}
            category="stop_window_change"
            updateComment={this.handleCommentInput}
            selectReasonCode={this.handleReasonSelect}
            updateCommentRequired={this.handleCommentRequired}
            useDescriptionAsValue // For reason codes that do not have an actual code
            setCodesRequired={this.setCodesRequired}
          />
        )}
      </CargomaticModal>)
    );
  }
}

export default withTranslation()(AddAppointmentModal);
